import { isNumber } from 'lodash';
import { getFramework } from '@/framework';
import { transferXYToAngle, translateAngleToXY } from '@/utils/math-kit';
import { transformFoundationVectorLayerDataToClient } from '@/utils/transform-kit';
import {
    MIN_IMG_SIZE,
    StretchOptionType,
    TextWritingType,
    VectorType,
    type ILayerBehavior,
    type ILayerOriginInfo,
    type IVectorLayerProps,
    type VectorTextInfo,
} from '@/version-types';
import {
    applyVectorTextLayout,
    calculateRealDistance,
    calculateVirtualDistance,
    getVectorLayerHost,
    normalizeVectorTextPadding,
    requestVectorSVGPreview,
    resolveVectorTextLayout,
} from '../utils';

type Behaviors = Pick<
    ILayerBehavior,
    | 'onBeforeResizeUpdate'
    | 'onBeforeScaleUpdate'
    | 'getActiveBaseInfo'
    | 'getScaleMinRatio'
    | 'getLayerMinSize'
    | 'getScaleIsNeedLimitSize'
    | 'whenGroupCanResize'
    | 'onBeforeGetLayerWithClone'
    | 'scaleOnlyUpdateBaseInfo'
    | 'resizeOnlyUpdateBaseInfo'
>;

/**
 * 按 Vector 整体缩放比例同步纯文字字号、字距与 padding。
 *
 * @param textInfo - 缩放前的 Vector Text 数据。
 * @param ratio - 本轮 Vector 宽度缩放比例。
 * @returns 保留其余整段文字样式的缩放结果。
 */
const scaleVectorTextInfo = (textInfo: VectorTextInfo, ratio: number): VectorTextInfo => ({
    ...textInfo,
    fontSize: textInfo.fontSize * ratio,
    letterSpacing: textInfo.letterSpacing * ratio,
    ...(textInfo.padding
        ? {
              padding: {
                  left: textInfo.padding.left * ratio,
                  right: textInfo.padding.right * ratio,
                  top: textInfo.padding.top * ratio,
                  bottom: textInfo.padding.bottom * ratio,
              },
          }
        : {}),
});

/**
 * 创建 Vector Layer 的尺寸与基础变换行为。
 *
 * @param identity - 当前编辑器实例标识。
 * @returns Vector Layer 操作行为集合。
 */
export const createOperationBehaviors = (identity: string) => {
    const framework = getFramework(identity);

    const behaviors: Behaviors = {
        /**
         * Resize 更新图层尺寸后同步 Vector 持久化数据。
         *
         * @param layer - 本次被原地更新的 Vector Layer。
         * @param options - Resize 原始信息与当前调整方向。
         * @returns 文字布局完成时返回最终尺寸与是否触及最小尺寸。
         */
        onBeforeResizeUpdate(layer, { originInfo, isChangeHeight }) {
            const layerData = layer.data as IVectorLayerProps;
            const { vectorType } = layer.data as IVectorLayerProps;

            // 防止矢量图层没有设置 proactiveWidth、proactiveHeight
            if (!layer.__proactiveWidth) layer.__proactiveWidth = layer.width;
            if (!layer.__proactiveHeight) layer.__proactiveHeight = layer.height;

            const host = layerData.textInfo?.text ? getVectorLayerHost(framework, layer.id) : null;
            let resolution = host?.layoutText(layer);
            let isMin = false;

            if (resolution?.status === 'ready') {
                const textInfo = layerData.textInfo!;
                const isHorizontal = textInfo.writingMode === TextWritingType.HORIZONTAL_TB;
                const padding = textInfo.padding;
                const inlinePadding = padding
                    ? isHorizontal
                        ? padding.left + padding.right
                        : padding.top + padding.bottom
                    : 0;
                const minInlineSize =
                    resolution.value.minInlineSize + resolution.value.listMarkerIndent + inlinePadding;
                const inlineSize = isHorizontal ? layer.width : layer.height;
                isMin = isChangeHeight !== isHorizontal && inlineSize <= minInlineSize;

                if (inlineSize < minInlineSize) {
                    if (isHorizontal) layer.width = minInlineSize;
                    else layer.height = minInlineSize;
                    // 最小尺寸修正会改变换行，交叉轴必须使用修正后的排版结果。
                    resolution = host?.layoutText(layer);
                }
            }

            // 矩形圆角变化
            if (vectorType === VectorType.Rect) {
                // 矩形，当圆角触发边界问题
                const halfSize = Math.round(Math.min(layer.width, layer.height) / 2);
                layerData.radius = originInfo.radius > halfSize ? halfSize : originInfo.radius;

                const { borderRadius } = layerData;
                if (borderRadius && originInfo.borderRadius) {
                    const { leftBottom, rightBottom, rightTop, leftTop } = originInfo.borderRadius;
                    layerData.borderRadius = {
                        leftBottom: leftBottom > halfSize ? halfSize : leftBottom,
                        rightBottom: rightBottom > halfSize ? halfSize : rightBottom,
                        rightTop: rightTop > halfSize ? halfSize : rightTop,
                        leftTop: leftTop > halfSize ? halfSize : leftTop,
                    };
                }
            }

            // 智能模式 每次用原始图片和当前图层大小算出比例
            const { stretchOption } = layerData;
            if (stretchOption === StretchOptionType.cover) {
                const { naturalWidth, naturalHeight, imageTransform } = layerData;
                const newWidthRatio = naturalWidth / layer.width;
                const newHeightRatio = naturalHeight / layer.height;

                const ratio = newWidthRatio < newHeightRatio ? newWidthRatio : newHeightRatio;

                imageTransform.widthRatio = naturalWidth / ratio / layer.width;
                imageTransform.heightRatio = naturalHeight / ratio / layer.height;
            }

            const textInfo = layerData.textInfo;
            const originPadding = (originInfo.data?.textInfo as VectorTextInfo | undefined)?.padding;
            // 空文字不会触发 Renderer layout；按面板现有上限收敛 padding，并从操作起点计算以支持回拉恢复。
            if (textInfo && !textInfo.text && originPadding) {
                textInfo.padding = normalizeVectorTextPadding(layer, textInfo, originPadding);
            }

            // 更新 proactive size
            if (isChangeHeight) {
                layer.__proactiveHeight = layer.height;
            } else {
                layer.__proactiveWidth = layer.width;
            }

            // Resize 的显式工作值与文字 API 共用真实宿主的 snapshot 和锚点公式。
            if (resolution) {
                Object.assign(layer, applyVectorTextLayout(layer, resolution));
                return { isMin, width: layer.width, height: layer.height };
            }
        },

        /**
         * Scale 连续阶段为含文字 Vector 发布当前组合 Preview，普通 Vector 继续复用旧 drawable。
         *
         * @param layer - 当前已按 scale 更新尺寸的 Vector Layer。
         * @returns 始终返回 true，表示连续阶段只更新正式 drawable 的基础信息。
         */
        scaleOnlyUpdateBaseInfo(layer) {
            if ((layer.data as IVectorLayerProps).textInfo?.text) {
                requestVectorSVGPreview(framework, 'update', layer);
            }

            return true;
        },

        /**
         * Resize 连续阶段通知 SVG preview 并只更新 Edgeless 基础信息，
         * 避免每次尺寸变化都生成新图片；最终尺寸仍由 Frame 在交互结束时构建正式资源。
         *
         * @param layer - 当前 Vector Layer。
         * @returns 始终返回 true，表示连续阶段只更新基础信息。
         */
        resizeOnlyUpdateBaseInfo(layer) {
            requestVectorSVGPreview(framework, 'update', layer);

            return true;
        },

        /**
         * 在整体缩放时同步 Vector 图形字段与纯文字字段，并用共享 commit 校正最终几何。
         *
         * @param layer - 本次被原地缩放的 Vector Layer。
         * @param info - 缩放起点数据与当前缩放来源。
         * @returns void。
         */
        onBeforeScaleUpdate(layer, info) {
            // prevLayerInfo 为开始缩放时部分图层信息
            // originInfo 这里一定有值
            const prevLayerInfo = info.originInfo as NonNullable<ILayerOriginInfo>;
            const layerData = layer.data as IVectorLayerProps;

            const originWidth = prevLayerInfo.width;
            const ratio = layer.width / originWidth;

            const radius = prevLayerInfo.radius;
            if (isNumber(layerData.radius) && isNumber(radius)) {
                const nextRadius = Math.floor(radius * ratio);
                layerData.radius = nextRadius;
            }

            if (prevLayerInfo.borderRadius) {
                const { leftBottom, rightBottom, rightTop, leftTop } = prevLayerInfo.borderRadius;
                layerData.borderRadius = {
                    leftBottom: Math.floor(leftBottom * ratio),
                    rightBottom: Math.floor(rightBottom * ratio),
                    rightTop: Math.floor(rightTop * ratio),
                    leftTop: Math.floor(leftTop * ratio),
                };
            }

            const nextStrokeWidth = prevLayerInfo.strokeWidth * ratio;
            layerData.strokeWidth = nextStrokeWidth >= 1 ? Math.round(nextStrokeWidth) : nextStrokeWidth; // 防止小于 1 时，strokeWidth 变成 0

            //阴影跟随图层位置
            if (layerData.vectorType !== VectorType.Graph) {
                const needUpdateShadow = layerData.effect.shadow?.[0] && layer.width;
                if (needUpdateShadow) {
                    const { x, y } = layerData.effect.shadow[0];
                    const angle = transferXYToAngle(x, y);
                    //原来的距离
                    const distance = calculateVirtualDistance(x, y, layer.width);
                    // 基于原来的距离 算出图层缩放后的距离
                    const _distance = calculateRealDistance(distance, layer.width);
                    const { x: x1, y: y1 } = translateAngleToXY(angle, _distance);
                    layerData.effect.shadow[0].x = x1;
                    layerData.effect.shadow[0].y = y1;
                }
            }

            // Foundation 数据在初始化前仍需按旧契约等比缩放文字字段；几何统一交给宿主 Renderer。
            const { textInfo } = layerData;
            if (textInfo) {
                const isInit = layer.__runtime?.isInit;
                // 每一帧都从操作起点缩放，避免连续 scale 复乘上一帧结果。
                const originTextInfo = (prevLayerInfo.data?.textInfo as VectorTextInfo | undefined) ?? textInfo;

                if (isInit) {
                    if (info.isFromAdjustScale) {
                        layerData.textInfo = scaleVectorTextInfo(originTextInfo, ratio);
                    }
                } else {
                    layerData.textInfo = scaleVectorTextInfo(originTextInfo, ratio);
                }
            }

            layer.__proactiveWidth = layer.width;
            layer.__proactiveHeight = layer.height;
            if (layerData.textInfo?.text) {
                const layoutCandidate = layer.__runtime?.isInit
                    ? layer
                    : { ...layer, data: transformFoundationVectorLayerDataToClient(layerData) };
                const resolved = resolveVectorTextLayout(framework, layoutCandidate);
                Object.assign(layer, {
                    left: resolved.left,
                    top: resolved.top,
                    width: resolved.width,
                    height: resolved.height,
                });
            }
        },

        getActiveBaseInfo(layer) {
            const layerData = layer.data as IVectorLayerProps;
            const { naturalHeight, naturalWidth, strokeWidth, radius, vectorType, borderRadius, textInfo } = layerData;

            const cutWidth = Math.min(naturalWidth, MIN_IMG_SIZE);
            const cutHeight = Math.min(naturalHeight, MIN_IMG_SIZE);

            return {
                cutWidth,
                cutHeight,
                strokeWidth,
                radius,
                vectorType,
                borderRadius,
                data: textInfo
                    ? { textInfo: { ...textInfo, padding: textInfo.padding ? { ...textInfo.padding } : undefined } }
                    : undefined,
            };
        },

        getScaleMinRatio(originInfo) {
            const { width, cutWidth } = originInfo;
            const offWidth = width - cutWidth;
            return (width - offWidth) / width;
        },

        getScaleIsNeedLimitSize(): boolean {
            return true;
        },

        whenGroupCanResize() {
            const horizontal = true;
            const vertical = true;
            return { horizontal, vertical };
        },

        onBeforeGetLayerWithClone(layer) {
            delete layer.__isOnTextEditing;
        },
    };

    return behaviors;
};
